/**
 * Operational documents domain API (Sprint Documents v1).
 *
 * Targets the org-scoped `/api/admin/documents` endpoints for the new
 * "Dokumente" page. Distinct from the entity-scoped helpers in
 * `documents.js` (unit/tenant/landlord/property/owner documents).
 *
 * Endpoints:
 *   POST   /api/admin/documents                  multipart upload
 *   GET    /api/admin/documents                  ?category=&search=&sort=&direction=&limit=&offset=
 *   GET    /api/admin/documents/summary          -> { total, category_counts }
 *   DELETE /api/admin/documents/{id}             204
 *   GET    /api/admin/documents/{id}/download    -> { url }
 */
import {
  API_BASE_URL,
  getApiHeaders,
  getApiHeadersMultipart,
  parseAdminErrorResponse,
} from "./core";

/**
 * GET /api/admin/documents
 *
 * `sort` and `direction` are server-side ordering, applied to the whole
 * filtered population before `limit`/`offset` cut a page out of it — so
 * page two really does continue page one. Both are closed vocabularies the
 * route declares as Literals; anything else comes back 422 rather than
 * being silently ignored. Omitting them yields newest-first, which is what
 * the endpoint did before it could be sorted at all.
 *
 * @param {{
 *   category?: string,
 *   search?: string,
 *   sort?: "uploaded_at" | "title" | "file_size_bytes",
 *   direction?: "asc" | "desc",
 *   limit?: number,
 *   offset?: number,
 * }} [params]
 * @returns {Promise<{ items: Array<object>, total: number }>} `total` counts
 *   the whole filtered population, not the page.
 */
export function fetchAdminOperationalDocuments(params = {}) {
  const sp = new URLSearchParams();
  if (params.category) sp.set("category", params.category);
  if (params.search) sp.set("search", params.search);
  if (params.sort) sp.set("sort", params.sort);
  if (params.direction) sp.set("direction", params.direction);
  if (params.limit != null) sp.set("limit", String(params.limit));
  // `offset` is written whenever it is given, including 0: `!= null` rather
  // than a truthiness test, so page 1 stays an explicit request rather than
  // an omission the server has to guess at.
  if (params.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/documents${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * GET /api/admin/documents/summary
 *
 * Folder stock: how many documents the organisation holds per category,
 * and in total. Deliberately takes NO parameters — not `category`, not
 * `search`, not `sort`, not `limit`/`offset`. A folder states what it
 * holds, not how many rows match what the reader is typing inside one of
 * them, which is also what lets the page fetch this once per mount rather
 * than once per debounce.
 *
 * `total` equals the sum of `category_counts` and is the same number the
 * list endpoint reports as its own `total` when called without filters.
 * Every whitelisted category is present including the zeroes, so a folder
 * does not vanish when it empties.
 *
 * @returns {Promise<{ total: number, category_counts: Record<string, number> }>}
 */
export function fetchAdminOperationalDocumentSummary() {
  return fetch(`${API_BASE_URL}/api/admin/documents/summary`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * POST /api/admin/documents
 * Multipart upload of a single document.
 *
 * @param {{
 *   file: File,
 *   title: string,
 *   category: string,
 *   related_to_label?: string,
 *   notes?: string,
 * }} input
 * @returns {Promise<object>} DocumentResponse
 */
export async function uploadAdminOperationalDocument({
  file,
  title,
  category,
  related_to_label,
  notes,
}) {
  if (!file) throw new Error("Datei fehlt.");
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("title", String(title || "").trim());
  fd.append("category", String(category || "").trim());
  const rel = related_to_label != null ? String(related_to_label).trim() : "";
  if (rel) fd.append("related_to_label", rel);
  const n = notes != null ? String(notes).trim() : "";
  if (n) fd.append("notes", n);

  const res = await fetch(`${API_BASE_URL}/api/admin/documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}

/**
 * GET /api/admin/documents/{id}/download
 * Returns a short-lived signed URL (15 min).
 */
export function fetchAdminOperationalDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** DELETE /api/admin/documents/{id} */
export async function deleteAdminOperationalDocument(documentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return { ok: true };
}
